import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Smartphone, Layers, Zap, Heart, ShieldCheck, Star, ArrowRight, Check } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1706510410399-e2473eeb51f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMHdhcm0lMjBsaWdodGluZ3xlbnwwfHx8fDE3ODY0OTc0NjZ8MA&ixlib=rb-4.1.0&q=85";

const AVANTAGES = [
  { icon: BookOpen, title: "Lecture pratique", desc: "Des guides clairs, structurés et faciles à consulter au quotidien." },
  { icon: Layers, title: "Contenu structuré", desc: "Une progression logique, par âge et par thème, pour trouver l'essentiel." },
  { icon: Zap, title: "Accès numérique immédiat", desc: "Recevez vos eBooks dès le paiement confirmé, sans attendre." },
  { icon: Smartphone, title: "Sur tous vos écrans", desc: "Consultez vos guides sur ordinateur, tablette et smartphone." },
  { icon: Heart, title: "Pensés pour les parents", desc: "Des guides conçus pour accompagner les parents avec bienveillance." },
];

const FAQ = [
  { q: "Puis-je télécharger mon eBook plusieurs fois ?", a: "Oui. Vous pouvez télécharger votre eBook plusieurs fois, mais uniquement depuis un seul appareil autorisé par achat." },
  { q: "Quels moyens de paiement sont disponibles ?", a: "Le paiement par carte bancaire est disponible et sécurisé. Apple Pay est proposé lorsque votre appareil et notre prestataire le permettent." },
  { q: "Est-ce un produit physique ?", a: "Non. Il s'agit exclusivement d'un eBook numérique au format PDF. Aucun produit physique ne sera envoyé." },
  { q: "Puis-je être remboursé ?", a: "Nous proposons commercialement une possibilité de remboursement sous 15 jours, sous réserve des conditions définies dans nos CGV. Cette garantie commerciale ne remplace pas le cadre légal applicable aux contenus numériques." },
];

export default function Home() {
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data });
  const ebooks = products.filter((p) => !p.is_pack);
  const pack = products.find((p) => p.is_pack);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-overline text-terracotta mb-4">La collection pour les parents</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              Accompagnez votre enfant, <span className="text-terracotta">de 0 à 6 ans</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-ink-muted leading-relaxed max-w-xl">
              Trois guides numériques pensés pour la parentalité et l'alimentation : des repères clairs, rassurants et pratiques, à garder toujours à portée de main.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button asChild size="lg" className="bg-terracotta hover:bg-terracotta-dark text-white rounded-md px-8" data-testid="hero-discover-collection">
                <Link to="/pack">Découvrir la collection</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-ink/20 text-ink hover:bg-cream rounded-md px-8" data-testid="hero-view-ebooks">
                <Link to="/boutique">Voir les eBooks</Link>
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-ink-muted">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sage" /> Paiement sécurisé</span>
              <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-sage" /> Accès immédiat</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
            <div className="relative flex justify-center items-end gap-3">
              {ebooks.slice(0, 3).map((b, i) => (
                <img key={b.id} src={b.cover_url} alt={b.title}
                  className={`book-shadow rounded-sm w-1/3 ${i === 1 ? "-mb-6 z-10 scale-110" : "opacity-95"}`} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* EBOOKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <Reveal className="max-w-2xl">
          <p className="font-overline text-terracotta mb-3">La collection</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-ink tracking-tight">Trois guides, une même exigence</h2>
          <p className="mt-4 text-ink-muted leading-relaxed">Chaque eBook peut s'acquérir seul ou dans le pack complet, à prix avantageux.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {ebooks.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="bg-cream border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <Reveal><h2 className="font-heading text-3xl sm:text-4xl text-ink tracking-tight text-center">Pourquoi nos guides ?</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 mt-12">
            {AVANTAGES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06} className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-sand border border-line flex items-center justify-center mb-4">
                  <a.icon className="h-5 w-5 text-terracotta" />
                </div>
                <h3 className="font-heading text-lg text-ink">{a.title}</h3>
                <p className="text-sm text-ink-muted mt-2 leading-relaxed">{a.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PACK */}
      {pack && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <div className="bg-ink text-sand rounded-xl overflow-hidden grid lg:grid-cols-2">
              <div className="p-10 sm:p-12 flex flex-col justify-center">
                <p className="font-overline text-terracotta-light mb-3">L'offre la plus complète</p>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">La Collection Complète</h2>
                <p className="mt-4 text-sand/75 leading-relaxed">Les 3 eBooks réunis : parentalité, alimentation et 80 recettes adaptées par âge. Tout ce qu'il faut, du quotidien à l'assiette.</p>
                <ul className="mt-6 space-y-2 text-sm">
                  {pack.pack_items?.map((c) => (
                    <li key={c.slug} className="flex items-center gap-2"><Check className="h-4 w-4 text-sage" /> {c.title}</li>
                  ))}
                </ul>
                <div className="flex items-center gap-4 mt-8">
                  <span className="text-3xl font-semibold text-white">{formatPrice(pack.price)}</span>
                  {pack.compare_price && <span className="text-lg text-sand/50 line-through">{formatPrice(pack.compare_price)}</span>}
                  {pack.compare_price && <span className="text-xs bg-sage text-white px-2 py-1 rounded-full">Économisez {formatPrice(pack.compare_price - pack.price)}</span>}
                </div>
                <Button asChild size="lg" className="mt-8 w-fit bg-terracotta hover:bg-terracotta-dark text-white rounded-md px-8" data-testid="home-buy-pack">
                  <Link to="/pack">Acheter la collection <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
              <div className="bg-terracotta/10 p-10 flex items-end justify-center gap-3">
                {pack.pack_items?.map((c, i) => (
                  <img key={c.slug} src={c.cover_url} alt={c.title} className={`book-shadow rounded-sm w-1/3 ${i === 1 ? "-mb-4 scale-110 z-10" : ""}`} />
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* AVIS */}
      <section className="bg-cream border-y border-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <Reveal><h2 className="font-heading text-3xl sm:text-4xl text-ink tracking-tight text-center">Ils nous font confiance</h2></Reveal>
          {reviews.length === 0 ? (
            <p className="text-center text-ink-muted mt-6 max-w-lg mx-auto">Les avis de nos lecteurs apparaîtront ici. Nous ne publions que des témoignages réels et vérifiés.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {reviews.slice(0, 6).map((r) => (
                <div key={r.id} className="bg-sand border border-line rounded-lg p-6" data-testid={`review-${r.id}`}>
                  <div className="flex gap-1 mb-3">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-terracotta text-terracotta" />)}</div>
                  <p className="text-ink-muted leading-relaxed text-sm">“{r.text}”</p>
                  <p className="mt-4 font-semibold text-ink text-sm">{r.author}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <Reveal><h2 className="font-heading text-3xl sm:text-4xl text-ink tracking-tight text-center mb-10">Questions fréquentes</h2></Reveal>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-heading text-lg text-ink">{f.q}</AccordionTrigger>
              <AccordionContent className="text-ink-muted leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
